/**
 * Live search for the FAQ page.
 *
 * Every question, answer and group is rendered server-side by
 * sections/beeul-faq-search.liquid, so the page is a complete, indexable FAQ
 * list before this ever runs, and stays a perfectly usable one if it never
 * does. All this adds is hiding rows that don't match what was typed, and
 * highlighting the words that did.
 *
 * Matching is AND across terms, folded so accents, punctuation and spacing
 * differences don't matter — "SGS-tested" and "sgs tested" find the same row.
 * It looks at the question, the answer text and the group name together, so
 * "delivery damaged" can find the returns question even though neither word
 * alone lives in its own copy.
 */

const WORD_CHARACTER = /[\p{L}\p{N}]/u;
const SCROLL_GAP = 16;

/**
 * Folds one character to its searchable form: accent stripped, lowercased,
 * anything that isn't a letter or digit turned into a space.
 *
 * Done per character rather than with a whole-string `.normalize('NFD')` —
 * NFD can split one visible character into two code units and would shift
 * every index after it. Taking only the base keeps the folded string exactly
 * as long as the source, which is what lets highlighting map a match found in
 * the folded text straight back onto the untouched label.
 */
function foldCharacter(character) {
  const base = character.normalize('NFD').charAt(0).toLowerCase();
  return WORD_CHARACTER.test(base) ? base : ' ';
}

/**
 * Precomputes both searchable forms of a string: `folded` (same length as
 * `source`, punctuation turned to spaces) and `squashed` (folded with the
 * spaces removed, so "SGS-tested" and "sgstested" line up too), plus a map
 * back from a squashed index to its source index for highlighting.
 */
function fold(source) {
  let folded = '';
  let squashed = '';
  const squashedToSource = [];

  for (let i = 0; i < source.length; i++) {
    const ch = foldCharacter(source.charAt(i));
    folded += ch;
    if (ch !== ' ') {
      squashed += ch;
      squashedToSource.push(i);
    }
  }

  return { source, folded, squashed, squashedToSource };
}

function parseTerms(query) {
  return fold(query).folded.split(' ').filter(Boolean);
}

function contains(text, term) {
  return text.folded.includes(term) || text.squashed.includes(term);
}

/** Every `[start, end)` range where `term` occurs in `text.source`. */
function findRanges(text, term) {
  if (!term) return [];

  const ranges = [];
  for (let at = text.folded.indexOf(term); at !== -1; at = text.folded.indexOf(term, at + term.length)) {
    ranges.push([at, at + term.length]);
  }

  // Only fall back to the squashed (separator-insensitive) form when the term
  // isn't present as typed, so "tested" highlights just that word rather than
  // swallowing the "SGS-" in front of it.
  if (ranges.length > 0) return ranges;

  for (let at = text.squashed.indexOf(term); at !== -1; at = text.squashed.indexOf(term, at + term.length)) {
    const start = text.squashedToSource[at];
    const end = text.squashedToSource[at + term.length - 1];
    if (start === undefined || end === undefined) break;
    ranges.push([start, end + 1]);
  }

  return ranges;
}

function mergeRanges(ranges) {
  if (ranges.length < 2) return ranges;
  const sorted = [...ranges].sort((a, b) => a[0] - b[0]);
  const merged = [sorted[0]];
  for (const [start, end] of sorted.slice(1)) {
    const previous = merged[merged.length - 1];
    if (start <= previous[1]) {
      previous[1] = Math.max(previous[1], end);
    } else {
      merged.push([start, end]);
    }
  }
  return merged;
}

/**
 * Rewrites `element` so the matched ranges of `source` sit inside <mark>.
 * Text goes through textContent, never innerHTML, so a question containing
 * markup-like characters can't inject anything.
 */
function highlight(element, source, ranges) {
  if (ranges.length === 0) {
    element.textContent = source;
    return;
  }

  const fragment = document.createDocumentFragment();
  let cursor = 0;

  for (const [start, end] of mergeRanges(ranges)) {
    if (start > cursor) fragment.append(source.slice(cursor, start));
    const mark = document.createElement('mark');
    mark.className = 'beeul-faq-search__match';
    mark.textContent = source.slice(start, end);
    fragment.append(mark);
    cursor = end;
  }

  if (cursor < source.length) fragment.append(source.slice(cursor));
  element.replaceChildren(fragment);
}

function prefersReducedMotion() {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

class BeeulFaqSearch extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('[data-faq-search-input]');
    this.status = this.querySelector('[data-faq-status]');
    this.empty = this.querySelector('[data-faq-empty]');
    this.clearButton = this.querySelector('[data-faq-clear]');
    this.clearInputButton = this.querySelector('[data-faq-clear-input]');

    this.rows = Array.from(this.querySelectorAll('[data-faq-row]')).map((row) => {
      const label = row.querySelector('[data-faq-label]');
      const answer = row.querySelector('[data-faq-answer]');
      const group = row.closest('[data-faq-group]');
      const groupName = group?.getAttribute('data-faq-group') ?? '';
      const labelSource = label?.textContent?.trim() ?? '';

      return {
        row,
        label,
        labelText: fold(labelSource),
        haystack: fold(`${labelSource} ${answer?.textContent ?? ''} ${groupName}`),
        group,
      };
    });

    this.groups = Array.from(this.querySelectorAll('[data-faq-group]'));
    this.pills = Array.from(this.querySelectorAll('[data-faq-pill]'));

    this.controller = new AbortController();
    const { signal } = this.controller;

    this.input?.addEventListener('input', () => this.apply(this.input.value), { signal });
    this.input?.addEventListener('keydown', this.handleKeydown, { signal });
    this.clearButton?.addEventListener('click', this.handleClear, { signal });
    this.clearInputButton?.addEventListener('click', this.handleClear, { signal });

    for (const pill of this.pills) {
      pill.addEventListener('click', this.handlePillClick, { signal });
    }

    // A ?q= in the address bar (a shared link, or a reload after searching)
    // wins over whatever the input already holds.
    const initialQuery = this.readQueryFromUrl() || this.input?.value || '';
    if (this.input) this.input.value = initialQuery;
    this.apply(initialQuery, { syncUrl: false });
  }

  disconnectedCallback() {
    this.controller?.abort();
  }

  handleKeydown = (event) => {
    if (event.key === 'Enter') {
      // Results are already live — Enter just gets the on-screen keyboard out
      // of the way and brings the first match into view.
      event.preventDefault();
      this.input?.blur();
      this.scrollToResults();
    } else if (event.key === 'Escape' && this.input?.value) {
      event.preventDefault();
      this.handleClear();
    }
  };

  handleClear = () => {
    if (this.input) this.input.value = '';
    this.apply('');
    this.input?.focus();
  };

  handlePillClick = (event) => {
    const pill = event.currentTarget;
    const value = pill.dataset.faqQuery || pill.textContent.trim();
    // Tapping the already-active pill clears the search, so it behaves like a
    // toggle rather than a one-way trip.
    const next = pill.getAttribute('aria-pressed') === 'true' ? '' : value;

    if (this.input) this.input.value = next;
    this.apply(next);
    if (next) this.scrollToResults();
  };

  apply(query, { syncUrl = true } = {}) {
    const terms = parseTerms(query);
    let matchCount = 0;

    for (const item of this.rows) {
      const isMatch = terms.length === 0 || terms.every((term) => contains(item.haystack, term));
      item.row.hidden = !isMatch;
      if (isMatch) matchCount++;

      if (item.label) {
        const ranges = isMatch ? terms.flatMap((term) => findRanges(item.labelText, term)) : [];
        highlight(item.label, item.labelText.source, ranges);
      }
    }

    // A group whose every row was filtered out would otherwise leave its
    // heading floating above nothing.
    for (const group of this.groups) {
      const hasVisibleRow = this.rows.some((item) => item.group === group && !item.row.hidden);
      group.hidden = !hasVisibleRow;
    }

    for (const pill of this.pills) {
      const value = pill.dataset.faqQuery || pill.textContent.trim();
      pill.setAttribute('aria-pressed', String(query !== '' && value.toLowerCase() === query.toLowerCase()));
    }

    this.updateStatus(query, matchCount);

    if (this.empty) this.empty.hidden = !(query !== '' && matchCount === 0);
    if (this.clearInputButton) this.clearInputButton.hidden = query === '';

    if (syncUrl) this.syncUrl(query);
  }

  updateStatus(query, count) {
    if (!this.status) return;

    if (query === '') {
      this.status.textContent = '';
      return;
    }

    const template = count === 1 ? this.dataset.resultsTextOne : this.dataset.resultsText;
    this.status.textContent = (template || '{count} results for "{query}"')
      .replace('{count}', String(count))
      .replace('{query}', query);
  }

  scrollToResults() {
    const target = this.groups.find((group) => !group.hidden) ?? this.empty;
    if (!target || target.hidden) return;

    // Same sticky-header clearance every in-page anchor on the site uses (see
    // .beeul-anchor) rather than measuring the header itself.
    const headerHeight = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--beeul-header-height')) || 60;
    const offset = headerHeight + SCROLL_GAP;
    const top = target.getBoundingClientRect().top + window.scrollY - offset;

    window.scrollTo({ top: Math.max(0, top), behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
  }

  readQueryFromUrl() {
    try {
      return new URL(window.location.href).searchParams.get('q') ?? '';
    } catch {
      return '';
    }
  }

  /** Keeps ?q= in step with the box so a search survives a reload and can be shared as a link. */
  syncUrl(query) {
    if (window.Shopify?.designMode) return;

    try {
      const url = new URL(window.location.href);
      if (query === '') {
        url.searchParams.delete('q');
      } else {
        url.searchParams.set('q', query);
      }
      window.history.replaceState(window.history.state, '', url);
    } catch {
      // A locked-down history API is no reason to break the filtering itself.
    }
  }
}

if (!customElements.get('beeul-faq-search')) {
  customElements.define('beeul-faq-search', BeeulFaqSearch);
}
